import { CourseLecture as ICourseLecture } from "@/api/models/course-lecture";

export class Tree<T> {
  readonly nodes: TreeNode<T>[];

  constructor(
    elements: {
      id: string;
      parentId: string | number | null;
      entity: T;
    }[]
  ) {
    this.nodes = elements.map(el => new TreeNode<T>(el.id, el.entity));

    for (const el of elements) {
      const node = this.getById(el.id);

      if (!node) {
        continue;
      }

      const parent = el.parentId ? this.getById(el.parentId) : null;

      if (parent) {
        node.setParent(parent);
      }
    }

    for (const node of this.nodes) {
      const children = elements
        .filter(el => el.parentId === node.id)
        .map(el => el.id)
        .map(id => this.getById(id));

      node.setChildren(children);
    }

    for (const node of this.nodes) {
      node.freeze();
    }
  }

  getById(id: string | number): TreeNode<T> {
    const filtered = this.nodes.filter((n: TreeNode<T>) => n.id === id);

    if (1 !== filtered.length) {
      throw new Error(`Node with id ${id} not exists.`);
    }

    return filtered[0];
  }

  getFirstLevelNodes(): TreeNode<T>[] {
    return this.nodes.filter(n => null === n.getParent());
  }

  getTreeItems(): TreeItem<T>[] {
    return this.getFirstLevelNodes().map(n => new TreeItem<T>(n));
  }
}

export class TreeNode<T> {
  readonly id: string;
  private parent: TreeNode<T> | null = null;
  private children: TreeNode<T>[] = [];
  readonly entity: T;
  private _freeze: boolean = false;

  constructor(id: string, entity: T) {
    this.id = id;
    this.entity = entity;
  }

  setParent(parent: TreeNode<T>): TreeNode<T> {
    if (!this._freeze) {
      this.parent = parent;
    }

    return this;
  }

  getParent(): TreeNode<T> | null {
    return this.parent;
  }

  setChildren(children: TreeNode<T>[]): TreeNode<T> {
    this.children = children;

    return this;
  }

  getChildren(): TreeNode<T>[] {
    return this.children;
  }

  freeze(): TreeNode<T> {
    this._freeze = true;

    return this;
  }
}

export class TreeItem<T> {
  readonly node: TreeNode<T>;
  readonly children: TreeItem<T>[];

  constructor(node: TreeNode<T>) {
    this.node = node;
    this.children = node.getChildren().map(c => new TreeItem<T>(c));
  }
}

export class FormattedLectureSort {
  private readonly lectures: Array<ICourseLecture>;
  private readonly currentLecture?: ICourseLecture;
  sortedArray: Array<ICourseLecture | undefined> = [];

  constructor(
    lectures: Array<ICourseLecture>,
    currentLecture?: ICourseLecture
  ) {
    this.lectures = lectures;
    if (currentLecture) {
      this.currentLecture = currentLecture;
    }
    this.sorted();
  }

  sorted() {
    let childes: any = [];
    let filteredArray: Array<ICourseLecture | undefined> = [];

    const firstLevelParent = this.lectures
      .filter(el => el.parentId === null || !el.parentId)
      .sort((a, b) => a.sort - b.sort);

    childes = this.lectures.filter(e => e.parentId !== null);

    let getChildren = (parent: ICourseLecture) => {
      filteredArray.push(parent);
      const sortedChildes: Array<ICourseLecture> | undefined = childes
        .filter((e: ICourseLecture) => e.parentId === parent.id)
        .sort((a: any, b: any) => a.sort - b.sort);

      if (sortedChildes?.length) {
        sortedChildes.forEach(el => {
          getChildren(el);
        });
      }
    };

    firstLevelParent.forEach(el => {
      getChildren(el);
    });

    this.sortedArray = filteredArray;
  }

  get nextLecture() {
    const index = this.sortedArray.findIndex(
      el => el?.id === this.currentLecture?.id
    );
    return this.sortedArray[index + 1];
  }

  get prevLecture() {
    const index = this.sortedArray.findIndex(
      el => el?.id === this.currentLecture?.id
    );
    return this.sortedArray[index - 1];
  }
}
